import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import ExtracurricularSubjectEditModal from './ExtracurricularSubjectEditModal';

/**
 * Справочник предметов внеурочных занятий. Простая таблица без привязки
 * к 1С (она для внеурочки не нужна — занятия бесплатные). Один раз
 * заполнили, дальше только поправки и архивирование.
 */
const URL_SCHEMA = { archive: '0', edit: '' };

export default function ExtracurricularSubjectsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('extracurricular.subjects', 'write');
  const canDelete = can('extracurricular.subjects', 'delete');

  const [params, setParams] = useUrlState(URL_SCHEMA);
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);

  const reload = () => {
    setLoad(true); setError(null);
    api.get('/api/extracurricular-subjects')
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  };
  useEffect(reload, []);

  const filtered = (params.archive === '1') ? rows : rows.filter((r) => !r.archive);

  // Группы предметов с одинаковым названием (регистр/лишние пробелы не в счёт) —
  // дубли параллельного ручного ввода. Ищем по всем, включая архив.
  const dupGroups = useMemo(() => {
    const byName = new Map();
    rows.forEach((r) => {
      const key = (r.name || '').trim().replace(/\s+/g, ' ').toLowerCase();
      if (!key) return;
      byName.set(key, (byName.get(key) || []).concat(r));
    });
    return [...byName.values()].filter((g) => g.length > 1);
  }, [rows]);

  const onMergeDuplicates = async () => {
    const names = dupGroups.map((g) => `«${g[0].name}» ×${g.length}`).join(', ');
    if (!confirm(
      `Объединить предметы с одинаковым названием?\n\n${names}\n\n`
      + 'В каждой группе останется один предмет, расписание и выборы родителей '
      + 'перенесутся на него, остальные записи удалятся. Действие необратимо.'
    )) return;
    try {
      const res = await api.post('/api/extracurricular-subjects/merge-duplicates', {});
      alert(`Готово: удалено дублей — ${res.removed}, перенесено занятий в расписании — ${
        (res.groups || []).reduce((s, g) => s + (g.slotsMoved || 0), 0)
      }, обновлено заявок родителей — ${res.selectionsUpdated}.`);
      reload();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось объединить дубли');
    }
  };

  const onDelete = async (s) => {
    if (!canDelete) return;
    if (!confirm(`Удалить предмет «${s.name}»? Если по нему уже есть расписание / зачисленные ученики — лучше архивировать.`)) return;
    try {
      await api.delete('/api/extracurricular-subjects/' + s.id);
      setRows((prev) => prev.filter((r) => r.id !== s.id));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось удалить');
    }
  };

  const editing = (() => {
    if (!params.edit) return null;
    if (params.edit === 'new') return 'new';
    return rows.find((r) => r.id === parseInt(params.edit, 10)) || null;
  })();

  return (
    <>
      <Topbar
        breadcrumb={['Внеурочные занятия', 'Предметы']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">
            Предметы {!loading && <span className="muted">· {filtered.length}</span>}
          </span>
          <select
            value={params.archive || '0'}
            onChange={(e) => setParams({ archive: e.target.value })}
            style={{
              marginLeft: 14, height: 30, padding: '0 8px',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
            }}
          >
            <option value="0">Активные</option>
            <option value="1">Все, включая архив</option>
          </select>
          <div className="ref-toolbar__spacer" />
          {canWrite && canDelete && dupGroups.length > 0 && (
            <button
              className="btn btn--sm"
              style={{ marginRight: 8 }}
              onClick={onMergeDuplicates}
              title="Слить предметы с одинаковым названием в один: расписание и выборы родителей перенесутся"
            >
              Объединить дубли ({dupGroups.length})
            </button>
          )}
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setParams({ edit: 'new' })}
            disabled={!canWrite}
            title={canWrite ? undefined : 'Нет прав на запись'}
          >
            <Icon name="plus" size={13} /> Добавить предмет
          </button>
        </div>

        <div className="ref-content">
          {error && <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : filtered.length === 0 ? (
            <div className="ref-empty">Предметов нет. Добавьте первый или импортируйте из старой системы (см. миграцию 0064).</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th style={{ width: 100 }}>Статус</th>
                    <th style={{ width: 80 }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr
                      key={s.id}
                      onClick={() => canWrite && setParams({ edit: String(s.id) })}
                      style={{ cursor: canWrite ? 'pointer' : 'default' }}
                    >
                      <td>
                        <div style={{ fontWeight: 500 }}>{s.name}</div>
                        {s.description && (
                          <div className="muted" style={{ fontSize: 12 }}>{s.description}</div>
                        )}
                      </td>
                      <td>
                        {s.archive
                          ? <span className="pay-st pay-st--mute">в архиве</span>
                          : <span className="pay-st pay-st--ok">активен</span>}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="ref-row-actions">
                          {canWrite && (
                            <button onClick={() => setParams({ edit: String(s.id) })} title="Редактировать">
                              <Icon name="edit" size={14} />
                            </button>
                          )}
                          {canDelete && (
                            <button className="is-danger" onClick={() => onDelete(s)} title="Удалить">
                              <Icon name="x" size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ExtracurricularSubjectEditModal
          mode={editing === 'new' ? 'create' : 'edit'}
          subject={editing === 'new' ? null : editing}
          onClose={() => setParams({ edit: '' })}
          onSaved={(s) => {
            setRows((prev) => {
              const i = prev.findIndex((r) => r.id === s.id);
              if (i >= 0) { const n = prev.slice(); n[i] = s; return n; }
              const next = [...prev, s];
              next.sort((a, b) => (a.archive - b.archive) || a.name.localeCompare(b.name));
              return next;
            });
          }}
        />
      )}
    </>
  );
}
